import { useEffect, useMemo, useState } from 'react'
import type { IntakeDomain } from '../../orders/api/intakeApi'
import {
  useMerchantFieldConfig,
  useSaveMerchantFieldConfig,
  useResetMerchantFieldConfig,
} from '../hooks/useFieldConfig'
import { validateFieldDefinitions } from '../utils/validateFieldDefinitions'
import { fromWorkingFields, makeUid, toWorkingFields, type WorkingField } from '../utils/workingFields'
import { FieldSetEditor } from './FieldSetEditor'
import { FieldPreview } from './FieldPreview'
import { Button } from '../../../components/ui/button'
import { Badge } from '../../../components/ui/badge'
// NOTE: icons limited to those declared in src/types/lucide-react.d.ts (local whitelist shim).
import { Save, RotateCcw, Eye, EyeOff } from 'lucide-react'

interface MerchantFieldConfigProps {
  merchantId: string
  domain: IntakeDomain
}

/**
 * "This merchant" tab under the TEMPLATE-COPY model.
 *
 * A merchant either has NO config (`fields === null` ⇒ the solution template applies
 * verbatim — shown here as a read-only preview) or its OWN fully-editable field list —
 * typically the template copied via "Customize for this merchant", then edited freely
 * (label, type, required, select options, validation, order) and extended with
 * merchant-specific fields.
 *
 * Load:  GET  → { catalog, fields, effective } (fields null ⇒ unconfigured).
 * Save:  PUT  { fields }.
 * Reset: DELETE ⇒ back to the template.
 */
export function MerchantFieldConfig({ merchantId, domain }: MerchantFieldConfigProps) {
  const { data, isLoading, error } = useMerchantFieldConfig(merchantId, domain)
  const saveMut = useSaveMerchantFieldConfig(merchantId, domain)
  const resetMut = useResetMerchantFieldConfig(merchantId, domain)

  const [workingFields, setWorkingFields] = useState<WorkingField[]>([])
  const [dirty, setDirty] = useState(false)
  /** True from "Customize for this merchant" until the draft is saved or cancelled. */
  const [draftCustomizing, setDraftCustomizing] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  const catalog = useMemo(() => data?.catalog ?? [], [data?.catalog])

  /** The merchant has its own saved field list on the server. */
  const isConfigured = data != null && data.fields != null
  /** The editor is shown: saved config, or an unsaved draft copy of the template. */
  const isEditing = isConfigured || draftCustomizing

  // Hydrate local state from the server (unless the user has unsaved changes).
  useEffect(() => {
    if (!data || dirty) return
    setWorkingFields(toWorkingFields(data.fields ?? []))
  }, [data, dirty])

  // ── Derived ────────────────────────────────────────────────────────────────
  const workingDefs = useMemo(() => fromWorkingFields(workingFields), [workingFields])
  const validationErrors = useMemo(() => validateFieldDefinitions(workingDefs), [workingDefs])
  const canSave = dirty && validationErrors.length === 0 && !saveMut.isPending

  /** Template fields whose key is not already in the working list — offered for copy-in. */
  const copyableTemplateFields = useMemo(() => {
    const usedKeys = new Set(workingFields.map((f) => f.key))
    return catalog.filter((f) => !usedKeys.has(f.key))
  }, [catalog, workingFields])

  // ── Actions ────────────────────────────────────────────────────────────────
  /** Copy the solution template into an editable working list for this merchant. */
  const handleCustomize = () => {
    setWorkingFields(toWorkingFields(catalog))
    setDraftCustomizing(true)
    setDirty(true)
  }

  /** Discard an unsaved draft copy — back to the read-only template state. No API call. */
  const handleCancelDraft = () => {
    setDraftCustomizing(false)
    setDirty(false)
    setWorkingFields([])
  }

  const handleChange = (next: WorkingField[]) => {
    setWorkingFields(next)
    setDirty(true)
  }

  /** Append one template field (not already in the list) to the merchant's working list. */
  const handleCopyTemplateField = (key: string) => {
    const def = catalog.find((f) => f.key === key)
    if (!def) return
    setWorkingFields((prev) => [...prev, { ...def, __uid: makeUid() }])
    setDirty(true)
  }

  const handleSave = () => {
    if (!canSave) return
    // Keep draftCustomizing as-is: isConfigured flips true once the refetch lands.
    saveMut.mutate(workingDefs, { onSuccess: () => setDirty(false) })
  }

  const handleReset = () => {
    if (
      !confirm(
        'Reset this merchant to the solution template? Its own field list — including all edits and merchant-specific fields — will be deleted. This cannot be undone.',
      )
    )
      return
    resetMut.mutate(undefined, {
      onSuccess: () => {
        setDirty(false)
        setDraftCustomizing(false)
      },
    })
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-muted-foreground">Loading merchant field config…</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-700">
          Failed to load field configuration: {(error as { message?: string }).message ?? 'Unknown error'}
        </p>
      </div>
    )
  }

  const templateFields = data?.effective ?? []

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Intake fields for this merchant</h3>
          <Badge variant={isEditing ? 'outline' : 'secondary'} className="text-[10px]">
            {isConfigured ? 'Customized' : draftCustomizing ? 'Customizing' : 'Solution template'}
          </Badge>
          {dirty && (
            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Unsaved</Badge>
          )}
        </div>
        {isEditing && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowPreview((v) => !v)}>
              {showPreview ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
              {showPreview ? 'Hide Preview' : 'Preview'}
            </Button>
            {isConfigured ? (
              <Button variant="ghost" size="sm" onClick={handleReset} disabled={resetMut.isPending}>
                <RotateCcw className="h-4 w-4 mr-1" />
                {resetMut.isPending ? 'Resetting…' : 'Reset to template'}
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={handleCancelDraft}>
                Cancel
              </Button>
            )}
            <Button size="sm" onClick={handleSave} disabled={!canSave}>
              <Save className="h-4 w-4 mr-1" />
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      {saveMut.isError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-xs text-red-700">
            Save failed: {(saveMut.error as { message?: string }).message ?? 'Unknown error'}.
          </p>
        </div>
      )}

      {resetMut.isError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-xs text-red-700">
            Reset failed: {(resetMut.error as { message?: string }).message ?? 'Unknown error'}.
          </p>
        </div>
      )}

      {!isEditing ? (
        /* ── Unconfigured: read-only template preview ─────────────────────────── */
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">Using solution template (default)</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                This merchant has no field configuration of its own — the solution template below
                applies verbatim, and template updates flow through automatically. Customizing copies
                the template into an independent, fully editable list for this merchant.
              </p>
            </div>
            <Button size="sm" onClick={handleCustomize} className="shrink-0">
              Customize for this merchant
            </Button>
          </div>

          {templateFields.length > 0 ? (
            <FieldPreview fields={templateFields} />
          ) : (
            <div className="rounded-lg border border-dashed border-border py-8 text-center">
              <p className="text-sm text-muted-foreground">
                The solution template for this domain is empty. Customize to add merchant-specific
                fields, or author the template in the “Solution catalog” tab.
              </p>
            </div>
          )}
        </>
      ) : (
        /* ── Configured (or draft): full editor over the merchant's own list ──── */
        <>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="min-w-0">
              <h4 className="text-sm font-medium text-foreground">Merchant field list</h4>
              <p className="text-xs text-muted-foreground">
                This merchant’s own copy — edit labels, types, options, validation and order, add or
                remove fields. The solution template is not affected.
              </p>
            </div>
            {copyableTemplateFields.length > 0 && (
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) handleCopyTemplateField(e.target.value)
                }}
                aria-label="Copy field from template"
                className="flex h-8 max-w-56 shrink-0 rounded-md border border-input bg-transparent px-2 py-1 text-xs text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="">Copy field from template…</option>
                {copyableTemplateFields.map((f) => (
                  <option key={f.key} value={f.key}>{f.label || f.key}</option>
                ))}
              </select>
            )}
          </div>

          {validationErrors.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-medium text-amber-800 mb-1">Fix before saving:</p>
              <ul className="list-disc list-inside text-xs text-amber-700 space-y-0.5">
                {validationErrors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}

          <FieldSetEditor
            fields={workingFields}
            onChange={handleChange}
            emptyLabel="No fields — customers would see an empty intake form."
            addLabel="Add field"
          />

          {/* Live effective preview = the working list */}
          {showPreview && (
            workingFields.length > 0 ? (
              <FieldPreview fields={workingDefs} />
            ) : (
              <div className="rounded-lg border border-dashed border-border py-6 text-center">
                <p className="text-sm text-muted-foreground">No fields — the intake form would be empty.</p>
              </div>
            )
          )}
        </>
      )}
    </div>
  )
}
